import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import _ from "lodash";
import { dummyPatientImages } from "Components/Pages/NewDashboard/PatientListCycles/PatientListCycles";

const initialState = {
  patientObject: {},
  loader: false,
  phaseCount: {},
  scheduleObject: {},
  scheduleLoader: false,
  showReminderModal: false,
  searchPatientList: [],
  searchPatientListLoading: false,
  isEditReminderModal: null,
  editReminderData: {},
  scheduleDate: moment().format("YYYY-MM-DD"),
  scheduleStartPage: 1,
  selectedPhase: null,
  filterOption: "5",
  locationOptions: [],
  filterLocation: null,
  patientListStartPage: 1,
  dashboardLoading: false,
  dataAnalysisExcelLoading: false,
  selectedPatientJourneyInfo: {},
  activePatient: {},
  // moduleWisePatientInfo: {},
  ongoingCyclePatientList: {},
  activePatientJourneyModuleDetails: {},
};

export const getPhseWisePatient = createAsyncThunk(
  "admin/get-patient-list-phase-wise",
  (props) => {
    const { start, limit, phase, location_id, payload } = props;

    return new Promise((resolve, reject) => {
      axios
        .post(
          `dashboard/patient-list/${location_id}/${start}/${limit}${
            phase ? `?phase=${phase}` : ""
          }`,
          payload
        )
        .then((res) => {
          if (res.data.err === 0) {
            if (res.data.data.dashboardPatientList.length > 0)
              resolve(res.data.data);
            else resolve([]);
          } else reject([]);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.msg);
          reject(error);
        });
    });
  }
);

export const getOngoingCyclePatientList = createAsyncThunk(
  "api/dashboard/ongoing-cycle/patient-list",
  async (props, { rejectWithValue }) => {
    const { location_id } = props;

    try {
      const url = `dashboard/ongoing-cycle/patient-list/${location_id}`;

      const res = await axios.post(url);

      if (res.data.err === 0) {
        let updatedList = res.data.data?.dashboardPatientList || [];

        updatedList = updatedList.map((patient) => {
          const randomIndex = Math.floor(
            Math.random() * dummyPatientImages.length
          );
          return {
            ...patient,
            dummy_patient_image: dummyPatientImages[randomIndex],
          };
        });

        return {
          dashboardPatientList: updatedList ?? [],
          dashboardPatientListCount:
            res.data.data?.dashboardPatientListCount ?? 0,
        };
      } else {
        return rejectWithValue([]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Something went wrong");
      return rejectWithValue(error);
    }
  }
);

export const getPhaseCount = createAsyncThunk(
  "admin/get-count-list",
  (location_id, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`dashboard/count/${location_id}`)
        .then((res) => {
          if (res.data.err === 0) {
            if (Object.keys(res.data.data).length > 0) resolve(res.data.data);
            else resolve([]);
          } else reject([]);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.msg);
          reject(error);
        });
    });
  }
);

export const getScheduleList = createAsyncThunk(
  "admin/get-schedule-list",
  (props, { dispatch }) => {
    const { start, limit, location_id, date, prevData = {} } = props;

    return new Promise((resolve, reject) => {
      axios
        .post(`dashboard/today-schedule/${location_id}/${start}/${limit}`, {
          location_id,
          schedule_date: date,
        })
        .then((res) => {
          if (res.data.err !== 0) return reject([]);

          const { data: resData } = res.data;
          const { scheduleList } = resData;

          if (!scheduleList?.length) return resolve([]);

          if (start === 1) return resolve(resData);

          if (
            prevData?.scheduleList?.length &&
            prevData.scheduleList.length % 10 === 0 &&
            scheduleList?.length > 0 &&
            !_.isEqual(scheduleList, prevData.scheduleList)
          ) {
            resData.scheduleList = [...prevData.scheduleList, ...scheduleList];
          }

          resolve(resData);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.msg);
          reject(error);
        });
    });
  }
);

export const getSearchPatientList = createAsyncThunk(
  "dashboard/get-search-patient-list",
  (props) => {
    return new Promise((resolve, reject) => {
      const { start, limit, payload } = props;

      axios
        .post(`dashboard/reminder/patient/list/${start}/${limit}`, payload)
        .then((res) => {
          if (res?.data?.err === 0) {
            const resData = res?.data?.data?.patient_list;

            if (Object.keys(resData).length > 0) {
              resolve(resData);
            } else {
              resolve([]);
            }
          } else {
            reject([]);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.msg);
          reject(error);
        });
    });
  }
);

export const addReminder = createAsyncThunk("add-reminder", (props) => {
  return new Promise((resolve, reject) => {
    const { location_id, payload } = props;

    axios
      .post(`dashboard/add-reminder/${location_id}`, payload)
      .then((res) => {
        if (res?.data?.err === 0) {
          toast.success(res.data?.msg);
          resolve(res.data);
        } else {
          toast.error(res?.data?.msg || "Failed to Add reminder.");
          reject({ message: res?.data?.msg });
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.msg || "Something went wrong.");
        reject(error);
      });
  });
});

export const getReminderData = createAsyncThunk(
  "get-reminder-data",
  (props) => {
    return new Promise((resolve, reject) => {
      const { location_id, id } = props;

      axios
        .get(`dashboard/view-reminder/${location_id}/${id}`)
        .then((res) => {
          if (res?.data?.err === 0) {
            if (res?.data?.data && Object.keys(res?.data?.data).length > 0) {
              resolve(res.data?.data);
            } else {
              resolve({});
            }
          } else {
            toast.error(res?.data?.msg || "Failed to fetch reminder data.");
            reject({});
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.msg || "Something went wrong.");
          reject(error);
        });
    });
  }
);

export const updateReminder = createAsyncThunk("update-reminder", (props) => {
  return new Promise((resolve, reject) => {
    const { location_id, id, payload } = props;

    axios
      .post(`dashboard/update-reminder/${location_id}/${id}`, payload)
      .then((res) => {
        if (res?.data?.err === 0) {
          toast.success(res.data?.msg);
          resolve(res.data);
        } else {
          toast.error(res?.data?.msg || "Failed to update reminder.");
          reject({ message: res?.data?.msg });
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.msg || "Something went wrong.");
        reject(error);
      });
  });
});

export const getDataAnalysisExcel = createAsyncThunk(
  "get-data-analysis-excel",
  (props) => {
    return new Promise((resolve, reject) => {
      const { start, limit, payload } = props;

      axios
        .post(`data-analysis/export/excel/${start}/${limit}`, payload)
        .then((res) => {
          if (res?.data?.err === 0) {
            window.open(res.data.data.url, "_blank");
            resolve(res.data);
          } else {
            toast.error(res?.data?.msg || "Failed to Export Excel.");
            reject({ message: res?.data?.msg });
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.msg || "Something went wrong.");
          reject(error);
        });
    });
  }
);

export const getDataAnalysisICSIRegister = createAsyncThunk(
  "get-data-analysis-export-ivf-register",
  (props) => {
    return new Promise((resolve, reject) => {
      const { start, limit, payload } = props;

      axios
        .post(`data-analysis/export/ivf-register/${start}/${limit}`, payload)
        .then((res) => {
          if (res?.data?.err === 0) {
            window.open(res.data.data.url, "_blank");
            resolve(res.data);
          } else {
            toast.error(
              res?.data?.msg || "Failed to Export ICSI Register Excel."
            );
            reject({ message: res?.data?.msg });
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.msg || "Something went wrong.");
          reject(error);
        });
    });
  }
);

export const getDashboardPatientInfo = createAsyncThunk(
  "get-dashboard-patient-info",
  (props) => {
    return new Promise((resolve, reject) => {
      const { location_id, patient_reg_id } = props;

      axios
        .post(`dashboard/patient-info/${location_id}/${patient_reg_id}`)
        .then((res) => {
          const { data } = res;

          if (data?.err === 0) {
            resolve(data?.data);
          } else {
            toast.error(data?.msg || "Failed to Export Excel.");
            reject({ message: data?.msg });
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.msg || "Something went wrong.");
          reject(error);
        });
    });
  }
);

export const dashboardViewPatientInfo = createAsyncThunk(
  "dashboard-view-patient-info",
  (props) => {
    return new Promise((resolve, reject) => {
      const { type, location_id, patient_reg_id, ivf_flow_id, module_id, _id } =
        props;

      axios
        .post(`dashboard/view/patient-info/${location_id}/${patient_reg_id}`, {
          _id,
          type,
          module_id,
          ivf_flow_id,
        })
        .then((res) => {
          const { data } = res;

          if (data?.err === 0) {
            resolve(data?.data);
          } else {
            toast.error(data?.msg || "Failed to Export Excel.");
            reject({ message: data?.msg });
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.msg || "Something went wrong.");
          reject(error);
        });
    });
  }
);

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setShowReminderModal: (state, action) => {
      state.showReminderModal = action.payload;
    },
    setSearchPatientList: (state, action) => {
      state.searchPatientList = action.payload;
    },
    setIsEditReminderModal: (state, action) => {
      state.isEditReminderModal = action.payload;
    },
    setEditReminderData: (state, action) => {
      state.editReminderData = action.payload;
    },
    setScheduleDate: (state, action) => {
      state.scheduleDate = action.payload;
    },
    setScheduleStartPage: (state, action) => {
      state.scheduleStartPage = action.payload;
    },
    setSelectedPhase: (state, action) => {
      state.selectedPhase = action.payload;
    },
    setFilterOption: (state, action) => {
      state.filterOption = action.payload;
    },
    setLocationOptions: (state, action) => {
      state.locationOptions = action.payload;
    },
    setFilterLocation: (state, action) => {
      state.filterLocation = action.payload;
    },
    setPatientListStartPage: (state, action) => {
      state.patientListStartPage = action.payload;
    },
    setActivePatient: (state, action) => {
      state.activePatient = action.payload;
    },
    // setModuleWisePatientInfo: (state, action) => {
    //   state.moduleWisePatientInfo = action.payload;
    // },
    setActivePatientJourneyModuleDetails: (state, action) => {
      state.activePatientJourneyModuleDetails = action.payload;
    },
    setSelectedPatientJourneyInfo: (state, action) => {
      state.selectedPatientJourneyInfo = action.payload;
    },
  },
  extraReducers: {
    [getPhseWisePatient.pending]: (state) => {
      state.loader = true;
    },
    [getPhseWisePatient.rejected]: (state) => {
      state.loader = false;
    },
    [getPhseWisePatient.fulfilled]: (state, action) => {
      state.patientObject = action.payload;
      state.loader = false;
    },
    [getOngoingCyclePatientList.pending]: (state) => {
      state.loader = true;
    },
    [getOngoingCyclePatientList.rejected]: (state) => {
      state.loader = false;
    },
    [getOngoingCyclePatientList.fulfilled]: (state, action) => {
      state.ongoingCyclePatientList = action.payload;
      state.loader = false;
    },
    [getPhaseCount.pending]: (state) => {
      state.phaseCount = {};
      state.loader = true;
    },
    [getPhaseCount.rejected]: (state) => {
      state.phaseCount = {};
      state.loader = false;
    },
    [getPhaseCount.fulfilled]: (state, action) => {
      state.phaseCount = action.payload;
      state.loader = false;
    },
    [getScheduleList.pending]: (state) => {
      state.scheduleLoader = true;
    },
    [getScheduleList.rejected]: (state) => {
      state.scheduleObject = {};
      state.scheduleLoader = false;
    },
    [getScheduleList.fulfilled]: (state, action) => {
      state.scheduleObject = action.payload;
      state.scheduleLoader = false;
    },
    [getSearchPatientList.pending]: (state) => {
      state.searchPatientListLoading = true;
    },
    [getSearchPatientList.rejected]: (state) => {
      state.searchPatientList = [];
      state.searchPatientListLoading = false;
    },
    [getSearchPatientList.fulfilled]: (state, action) => {
      state.searchPatientList = action.payload;
      state.searchPatientListLoading = false;
    },
    [getReminderData.rejected]: (state) => {
      state.editReminderData = {};
    },
    [getReminderData.fulfilled]: (state, action) => {
      state.editReminderData = action.payload;
    },
    [getDashboardPatientInfo.pending]: (state) => {
      state.dashboardLoading = true;
    },
    [getDashboardPatientInfo.rejected]: (state) => {
      state.dashboardLoading = false;
    },
    [getDashboardPatientInfo.fulfilled]: (state, action) => {
      state.selectedPatientJourneyInfo = action.payload;
      state.dashboardLoading = false;
    },
    [getReminderData.fulfilled]: (state, action) => {
      state.editReminderData = action.payload;
    },
    [dashboardViewPatientInfo.pending]: (state) => {
      state.dashboardLoading = true;
    },
    [dashboardViewPatientInfo.rejected]: (state) => {
      state.dashboardLoading = false;
    },
    [dashboardViewPatientInfo.fulfilled]: (state, action) => {
      state.activePatientJourneyModuleDetails = action.payload;
      state.dashboardLoading = false;
    },
    [getDataAnalysisExcel.pending]: (state) => {
      state.dataAnalysisExcelLoading = true;
    },
    [getDataAnalysisExcel.rejected]: (state) => {
      state.dataAnalysisExcelLoading = false;
    },
    [getDataAnalysisExcel.fulfilled]: (state, action) => {
      state.dataAnalysisExcelLoading = false;
    },
    [getDataAnalysisICSIRegister.pending]: (state) => {
      state.dataAnalysisExcelLoading = true;
    },
    [getDataAnalysisICSIRegister.rejected]: (state) => {
      state.dataAnalysisExcelLoading = false;
    },
    [getDataAnalysisICSIRegister.fulfilled]: (state, action) => {
      state.dataAnalysisExcelLoading = false;
    },
  },
});

export const {
  setShowReminderModal,
  setSearchPatientList,
  setIsEditReminderModal,
  setEditReminderData,
  setScheduleDate,
  setScheduleStartPage,
  setSelectedPhase,
  setFilterOption,
  setLocationOptions,
  setFilterLocation,
  setPatientListStartPage,
  setActivePatient,
  // setModuleWisePatientInfo,
  setSelectedPatientJourneyInfo,
  setActivePatientJourneyModuleDetails,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
